/**
 * Progress sub-dialog for `Create Blueprint`.
 *
 * Presentational — the parent setup dialog owns the in-flight operation state
 * and feeds it in through props. Renders:
 *   • RandomDotmSpinner while running
 *   • indeterminate / determinate progress bar
 *   • auto-scrolling log of each step
 *   • bottom action button that switches between Abort / Back / Open based on
 *     the operation's lifecycle status.
 *
 * Blocks pointer events on the underlying setup dialog by virtue of being a
 * Radix `Dialog` — fixes the bug where clicking "Create Blueprint" still left
 * the setup dialog fully interactive while work was happening.
 */

import { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { cn } from '@/lib/utils';
import type {
  BlueprintObjectCounts,
  BlueprintSetupPhase,
} from '../types/databaseBlueprintTypes';

export type BlueprintCreationStatus =
  | 'running'
  | 'aborting'
  | 'aborted'
  | 'error'
  | 'done';

export type BlueprintCreationLogEntry = {
  id: number;
  phase: BlueprintSetupPhase;
  message: string;
};

export type BlueprintCreationSummary = {
  folderPath: string;
  counts: BlueprintObjectCounts;
};

type Props = {
  open: boolean;
  status: BlueprintCreationStatus;
  phase: BlueprintSetupPhase | null;
  log: ReadonlyArray<BlueprintCreationLogEntry>;
  /** 1-based current file index during the writing phase; null otherwise. */
  currentIndex: number | null;
  totalCount: number | null;
  /** Populated when status === 'done'. */
  summary: BlueprintCreationSummary | null;
  /** Populated when status === 'error' or 'aborted' with a non-empty error. */
  errorMessage: string | null;
  onAbort: () => void;
  /**
   * Called when the user clicks the trailing action button (Back to setup or
   * Open Blueprint). The parent decides what each transition means; this dialog
   * just reports the click.
   */
  onClose: () => void;
};

const PHASE_HEADLINE: Record<BlueprintSetupPhase, string> = {
  preparing: 'Preparing blueprint folder',
  fetching: 'Reading schema from database',
  writing: 'Writing .sql files',
  metadata: 'Writing manifest & baseline',
  aborting: 'Aborting',
  done: 'Blueprint created',
  aborted: 'Blueprint creation aborted',
  error: 'Blueprint creation failed',
};

export function BlueprintCreationProgressDialog({
  open,
  status,
  phase,
  log,
  currentIndex,
  totalCount,
  summary,
  errorMessage,
  onAbort,
  onClose,
}: Props) {
  const logScrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll the log to the bottom whenever a new entry lands so the user
  // sees the latest step without having to reach for the scroll bar.
  useEffect(() => {
    const root = logScrollRef.current;
    if (!root) return;
    // shadcn's ScrollArea wraps the content in `[data-radix-scroll-area-viewport]`.
    const viewport = root.querySelector<HTMLDivElement>(
      '[data-radix-scroll-area-viewport]',
    );
    const target = viewport ?? root;
    target.scrollTop = target.scrollHeight;
  }, [log.length]);

  const isRunning = status === 'running' || status === 'aborting';
  const showDeterminateBar =
    status === 'running' &&
    (phase === 'writing' || phase === 'fetching') &&
    !!totalCount &&
    totalCount > 0;
  const percent =
    showDeterminateBar && currentIndex != null
      ? Math.min(100, Math.round((currentIndex / (totalCount ?? 1)) * 100))
      : 0;

  // Disallow accidental dismissal of the dialog while we're working. The user
  // must explicitly Abort to bail, then click "Back to setup".
  const handleOpenChange = (next: boolean) => {
    if (next) return;
    if (isRunning) return; // ignore Escape / outside-click while running
    onClose();
  };

  const renderActionButton = () => {
    if (status === 'running') {
      return (
        <Button
          type="button"
          variant="destructive"
          onClick={onAbort}
          data-testid="blueprint-creation-abort"
        >
          Abort
        </Button>
      );
    }
    if (status === 'aborting') {
      return (
        <Button
          type="button"
          variant="destructive"
          disabled
          data-testid="blueprint-creation-aborting"
        >
          <RandomDotmSpinner size={16} className="mr-1" />
          Aborting…
        </Button>
      );
    }
    if (status === 'done') {
      return (
        <Button
          type="button"
          onClick={onClose}
          data-testid="blueprint-creation-open"
        >
          Open Blueprint
        </Button>
      );
    }
    // aborted | error
    return (
      <Button
        type="button"
        onClick={onClose}
        data-testid="blueprint-creation-back"
      >
        Back to setup
      </Button>
    );
  };

  const renderSummary = () => {
    if (!summary) return null;
    const total =
      summary.counts.tables +
      summary.counts.views +
      summary.counts.procedures +
      summary.counts.functions +
      summary.counts.triggers;
    const lines: Array<{ label: string; n: number }> = [
      { label: 'Tables', n: summary.counts.tables },
      { label: 'Views', n: summary.counts.views },
      { label: 'Stored procedures', n: summary.counts.procedures },
      { label: 'Functions', n: summary.counts.functions },
      { label: 'Triggers', n: summary.counts.triggers },
      { label: 'User-defined types', n: summary.counts.userTypes },
      { label: 'Sequences', n: summary.counts.sequences },
      { label: 'Synonyms', n: summary.counts.synonyms },
      { label: 'Permissions', n: summary.counts.permissions },
      { label: 'Schemas', n: summary.counts.schemas },
      { label: 'Packages', n: summary.counts.packages },
    ].filter((l) => l.n > 0);
    return (
      <div
        className="rounded-sm border bg-muted/30 p-3 text-sm space-y-1.5"
        data-testid="blueprint-creation-summary"
      >
        <div className="text-sm font-medium">
          Wrote {total} object{total === 1 ? '' : 's'}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {lines.map((l) => (
            <div key={l.label} className="flex justify-between">
              <span>{l.label}</span>
              <span className="font-medium text-foreground">{l.n}</span>
            </div>
          ))}
        </div>
        <div
          className="text-xs text-muted-foreground truncate pt-1"
          title={summary.folderPath}
        >
          Folder:{' '}
          <code className="text-foreground">{summary.folderPath}</code>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-4xl w-[min(64rem,calc(100vw-2rem))]"
        data-testid="blueprint-creation-progress-dialog"
        // Don't let Esc / outside-click dismiss while running; only the Abort
        // button can stop it.
        onEscapeKeyDown={(e) => {
          if (isRunning) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isRunning) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRunning && <RandomDotmSpinner size={16} />}
            {phase ? PHASE_HEADLINE[phase] : 'Creating blueprint'}
          </DialogTitle>
          <DialogDescription>
            {status === 'running' && phase === 'fetching' && totalCount != null && totalCount > 0 ? (
              <>
                Fetched metadata for {currentIndex ?? 0} / {totalCount} objects…
              </>
            ) : (
              status === 'running' &&
              phase === 'fetching' && (
                <>Fetching schema metadata can take a moment for large databases.</>
              )
            )}
            {status === 'running' && phase === 'writing' && totalCount != null && (
              <>
                Writing {currentIndex ?? 0} / {totalCount} files…
              </>
            )}
            {status === 'running' && (phase === 'preparing' || phase === 'metadata') && (
              <>Working — this should be quick.</>
            )}
            {status === 'aborting' && (
              <>Waiting for the current step to finish, then rolling back…</>
            )}
            {status === 'aborted' && (
              <>No files were left behind. Your setup choices are still there.</>
            )}
            {status === 'error' && (
              <>{errorMessage ?? 'Something went wrong while creating the blueprint.'}</>
            )}
            {status === 'done' && (
              <>Blueprint is ready. Click Open Blueprint to switch to the workspace.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Progress bar */}
          {isRunning && (
            <div
              className="h-1.5 w-full overflow-hidden rounded-sm bg-muted"
              data-testid="blueprint-creation-progress-bar"
            >
              {showDeterminateBar ? (
                <div
                  className="h-full bg-primary transition-[width] duration-150 ease-linear"
                  style={{ width: `${percent}%` }}
                  data-testid="blueprint-creation-progress-fill"
                  aria-label={`Progress: ${percent}%`}
                />
              ) : (
                <div
                  className="h-full w-1/3 bg-primary/70 animate-pulse"
                  aria-label="Working"
                />
              )}
            </div>
          )}

          {/* Summary (done only) */}
          {status === 'done' && renderSummary()}

          {/* Error banner */}
          {status === 'error' && errorMessage && (
            <div
              className="rounded-sm border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
              data-testid="blueprint-creation-error"
            >
              {errorMessage}
            </div>
          )}

          {/* Scrolling log */}
          <ScrollArea
            ref={logScrollRef}
            className="h-[240px] w-full border rounded-sm bg-muted/20"
            data-testid="blueprint-creation-log"
          >
            <ul className="p-2 space-y-0.5 font-mono text-[11px] leading-tight max-w-full">
              {log.length === 0 ? (
                <li className="text-muted-foreground">Waiting for first event…</li>
              ) : (
                log.map((entry) => (
                  <li
                    key={entry.id}
                    className={cn(
                      'flex gap-2 items-baseline min-w-0',
                      entry.phase === 'error' && 'text-destructive',
                      entry.phase === 'done' && 'text-foreground font-semibold',
                      entry.phase === 'aborted' && 'text-muted-foreground italic',
                      entry.phase === 'aborting' && 'text-muted-foreground italic',
                    )}
                  >
                    <span className="uppercase tracking-wide text-[9px] text-muted-foreground shrink-0 w-[60px]">
                      {entry.phase}
                    </span>
                    <span className="truncate min-w-0 flex-1" title={entry.message}>
                      {entry.message}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </ScrollArea>
        </div>

        <DialogFooter>{renderActionButton()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
